import axios from 'axios';
import { getContentCMS } from 'core/crud/contents.crud';
import { CONTENT_TYPES } from 'core/variables/constant';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function usePdfContent({ contentGuid, onDocumentLoadSuccess, pageNumber, pagesRange, url, variant, contentType }) {
  const { t } = useTranslation();
  const [urlResource, setUrlResource] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [availablePages, setAvailablePages] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [containerSize, setContainerSize] = useState(0);
  const containerRef = useRef(null);

  const updateSize = useCallback(() => {
    if (containerRef.current) {
      const size = variant === 'full-height' ? containerRef.current.clientHeight : containerRef.current.clientWidth;
      setContainerSize(size);
    }
  }, [variant]);

  useEffect(() => {
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [updateSize]);

  useEffect(() => {
    if (url) {
      setUrlResource(url);
    }
  }, [url]);

  useEffect(() => {
    let cancelled = false;

    async function getContent() {
      const response = await getContentCMS(contentGuid);
      const contentData = response?.data?.data;
      const pathPdf = contentData?.url ? contentData.url.replace('.xod', '.pdf') : null;

      if (contentData?.is_public) {
        if (cancelled) return;
        if (pathPdf) {
          setUrlResource(pathPdf);
        } else if (!cancelled) {
          setHasError(true);
        }
        return;
      }

      if (!pathPdf) {
        if (!cancelled) setHasError(true);
        return;
      }

      let responseFilesStorage = await axios.get(`..${pathPdf}?set-cookie-in=header&auth=signed-url`, {
        maxRedirects: 0,
      });
      let responseCookies = responseFilesStorage?.data?.data?.cookies || [];
      responseCookies.forEach((cookie) => {
        document.cookie = cookie.replace('; Secure; HttpOnly; SameSite=None', '');
      });
      let responseUrl = responseFilesStorage?.data?.data?.url;

      if (cancelled) return;

      if (responseUrl) {
        setUrlResource(responseUrl);
      } else {
        setHasError(true);
      }
    }

    if (contentGuid) {
      setUrlResource(null);
      setHasError(false);
      getContent().catch(() => {
        if (!cancelled) setHasError(true);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [contentGuid]);

  useEffect(() => {
    const handleTotalPageCount = (event) => {
      setNumPages(event.detail.totalPageCount);
    };

    window.addEventListener('nutrient:totalPageCount', handleTotalPageCount);

    return () => {
      window.removeEventListener('nutrient:totalPageCount', handleTotalPageCount);
    };
  }, []);

  function onLoadSuccess(pdf) {
    setNumPages(pdf.numPages);
    if (onDocumentLoadSuccess) {
      onDocumentLoadSuccess(pdf);
    }
  }

  function onLoadError() {
    setHasError(true);
  }

  function goToPreviousPage() {
    if (contentType === CONTENT_TYPES.OFFICE) {
      window.dispatchEvent(new CustomEvent('nutrient:gotoprev'));
    }

    if (availablePages && availablePages.length > 0) {
      const currentIndex = availablePages.indexOf(currentPage);
      if (currentIndex > 0) {
        setCurrentPage(availablePages[currentIndex - 1]);
      }
    } else {
      setCurrentPage((prev) => Math.max(prev - 1, 1));
    }
  }

  function goToNextPage() {
    if (contentType === CONTENT_TYPES.OFFICE) {
      window.dispatchEvent(new CustomEvent('nutrient:gotonext'));
    }

    if (availablePages && availablePages.length > 0) {
      const currentIndex = availablePages.indexOf(currentPage);
      if (currentIndex < availablePages.length - 1) {
        setCurrentPage(availablePages[currentIndex + 1]);
      }
    } else {
      setCurrentPage((prev) => Math.min(prev + 1, numPages ?? prev));
    }
  }

  // Sync availablePages and currentPage when pagesRange or pageNumber changes
  const rangePages = useMemo(() => generatePagesFromRange(pagesRange), [pagesRange]);

  useEffect(() => {
    if (rangePages && rangePages.length > 0) {
      setAvailablePages(rangePages);
      setCurrentPage(rangePages[0]);
    } else if (pageNumber) {
      setAvailablePages(null);
    } else {
      setAvailablePages(null);
    }
  }, [rangePages, pageNumber]);

  // Determinar qué páginas mostrar
  let pagesToRender = [currentPage];
  let showNavigation = false;
  let currentPageForDisplay = currentPage;
  let totalPagesForDisplay = numPages;

  if (rangePages && rangePages.length > 0) {
    pagesToRender = [currentPage];
    showNavigation = true;

    const currentIndex = rangePages.indexOf(currentPage);
    currentPageForDisplay = currentIndex >= 0 ? currentIndex + 1 : 1;
    totalPagesForDisplay = rangePages.length;
  } else if (pageNumber) {
    pagesToRender = [pageNumber];
    currentPageForDisplay = pageNumber;
  } else {
    pagesToRender = [currentPage];
    showNavigation = true;
  }

  // Hasta que el PDF carga, o si falla, la región se queda sin nombre (y sin landmark): con rango el total
  // sale de `pagesRange` y no de la carga, así que sin `numPages` se anunciaría una región vacía.
  const hasLoadedPages = !hasError && Boolean(numPages);
  const pageRegionLabel = hasLoadedPages ? t('common:Page x of y', { current: currentPageForDisplay, total: totalPagesForDisplay }) : undefined;

  const isPreviousDisabled = availablePages ? availablePages.indexOf(currentPage) <= 0 : currentPage <= 1;
  const isNextDisabled = availablePages ? availablePages.indexOf(currentPage) >= availablePages.length - 1 : !numPages || currentPage >= numPages;

  return {
    containerRef,
    containerSize,
    currentPageForDisplay,
    goToNextPage,
    goToPreviousPage,
    hasError,
    isNextDisabled,
    isPreviousDisabled,
    onLoadError,
    onLoadSuccess,
    pageRegionLabel,
    pagesToRender,
    showNavigation,
    totalPagesForDisplay,
    urlResource,
  };
}

function generatePagesFromRange(range) {
  if (!range || !Array.isArray(range) || range.length < 2) {
    return null;
  }
  const pages = [];
  for (let i = range[0]; i <= range[1]; i++) {
    pages.push(i);
  }
  return pages;
}

export default usePdfContent;
